import React from "react";
import { motion } from "framer-motion";
import { Bot, Paintbrush, LineChart, Megaphone, Cpu, Microchip } from "lucide-react";

/** Fallback if Paintbrush import is undefined */
const PaintbrushFallback = ({ size = 48 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    style={{ display: "block" }}
  >
    <path
      d="M3 21c.6-1.6 2.6-3 5-3 2.4 0 4.4 1.4 5 3"
      stroke="#E6F7FF"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.5 5.5c1.5 1.5 2 3.5 1 4.5l-6 6c-1 1-2.9.6-4.5-1L6 12"
      stroke="#E6F7FF"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Icon wrapper */
function IconWrap({ children }) {
  return (
    <div className="w-16 h-16 flex items-center justify-center mb-6">
      {children}
    </div>
  );
}

/* 🔥 IMAGE WALA CONTENT — ONLY THIS CHANGED */
const solutions = [
  {
    icon: Paintbrush ? (
      <Paintbrush size={48} strokeWidth={1.6} color="#87CEEB" />
    ) : (
      <PaintbrushFallback size={48} />
    ),
    title: "Concept & Design",
    desc: "Turning ideas into blueprints",
  },
  {
    icon: <Cpu size={48} strokeWidth={1.6} color="#1E90FF" />,
    title: "Embedded Systems",
    desc: "Hardware & firmware development",
  },
  {
    icon: <Bot size={48} strokeWidth={1.6} color="#87CEEB" />,
    title: "AI / ML Integration",
    desc: "Smart tech implementation",
  },
  {
    icon: <Microchip size={48} strokeWidth={1.6} color="#1E90FF" />,
    title: "PCB Solutions",
    desc: "Design, fabrication & assembly",
  },
  {
    icon: <LineChart size={48} strokeWidth={1.6} color="#87CEEB" />,
    title: "CAD Design",
    desc: "Enclosures & mechanical modeling",
  },
  {
    icon: <Megaphone size={48} strokeWidth={1.6} color="#1E90FF" />,
    title: "Tech Consulting",
    desc: "Expert guidance & support",
  },
];

function Solutions() {
  return (
    <section
      id="our-solution"
      className="relative text-gray-900 overflow-visible bg-gradient-to-r from-[#0A2A43] via-[#1E90FF] to-[#87CEEB] py-20"
    >
      {/* Background texture */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/circuits.png')] opacity-10"></div>

      {/* Glow border */}
      <div className="absolute inset-0 rounded-[50px] border-2 border-blue-400/50 shadow-[0_0_40px_10px_rgba(30,144,255,0.3)] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
        {/* Title */}
        <div className="text-center mb-12">
          <motion.h2
            className="text-4xl md:text-5xl font-extrabold mb-4 text-blue-50"
            initial={{ opacity: 0, y: -40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Our <span className="text-blue-200">Solutions</span>
          </motion.h2>

          <motion.p
            className="text-blue-50/90 max-w-2xl mx-auto text-lg"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            We deliver futuristic robotics and AI-powered technology solutions to transform businesses worldwide.
          </motion.p>
        </div>

        {/* Boxes grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              className="bg-blue-500/20 backdrop-blur-md rounded-2xl p-8 shadow-lg border border-blue-400/40 hover:border-blue-300 transition-all duration-500 hover:-translate-y-3"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
            >
              <IconWrap>{solution.icon}</IconWrap>
              <h3 className="text-xl font-semibold text-blue-50 mb-3 text-center">
                {solution.title}
              </h3>
              <p className="text-blue-100/90 text-center">
                {solution.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Solutions;
